import { motion, useInView } from "framer-motion";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SPRING_BOUNCE, timelineVariants } from "./animations";
import { useGlobalState, isLoggedIn } from "../../../GlobalStates";
import juniorDuck from "../../../assets/images/juniorduckstatic.png";
import seniorDuck from "../../../assets/images/seniorduckstatic.png";
import superDuck from "../../../assets/images/superduckstatic.png";
import axios from "../../../axios";

const STEPS = [
  { key: "step_1", duck: juniorDuck, dateField: "applyByDate" },
  { key: "step_2", duck: seniorDuck, dateField: "hearFromUsDate" },
  { key: "step_3", duck: superDuck, dateField: "interviewDate" },
];

const formatDate = (isoDate, language) => {
  const date = new Date(isoDate);
  const day = date.getDate();
  const month = date.getMonth();

  if (language.startsWith("zh")) {
    const monthNames = [
      "1月",
      "2月",
      "3月",
      "4月",
      "5月",
      "6月",
      "7月",
      "8月",
      "9月",
      "10月",
      "11月",
      "12月",
    ];
    return `${monthNames[month]}${day}日`;
  }

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  return `${ordinal(day)} ${monthNames[month]}`;
};

const CallToReflection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [recruitmentDates, setRecruitmentDates] = useState(null);

  useEffect(() => {
    axios
      .get("/v1/leader/recruitment-dates")
      .then((res) => setRecruitmentDates(res.data))
      .catch((err) => console.error("Failed to fetch recruitment dates:", err));
  }, []);

  const getStepText = (step) => {
    if (!step.dateField || !recruitmentDates) {
      return t(`leader_programme.cta.timeline.${step.key}`);
    }
    const formattedDate = formatDate(recruitmentDates[step.dateField], i18n.language);
    return t(`leader_programme.cta.timeline.${step.key}`, { date: formattedDate });
  };

  const handleApplyClick = () => {
    const applyPath = "/our-leader-programme/apply";
    if (!isLoggedIn(user)) {
      window.location = `/login?reroute=${applyPath}`;
    } else {
      navigate(applyPath);
    }
  };

  return (
    <section ref={sectionRef} className="tw-bg-gray-900 tw-py-16 tw-text-white md:tw-py-24">
      <div className="tw-mx-auto tw-max-w-5xl tw-px-6 tw-text-center md:tw-px-16 lg:tw-px-32">
        {/* Main question */}
        <motion.h2
          className="tw-mb-6 tw-text-[26px] tw-font-extrabold tw-leading-tight md:tw-text-4xl lg:tw-text-5xl"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={SPRING_BOUNCE}
        >
          {t("leader_programme.cta.headline")}
        </motion.h2>

        {/* Application flow timeline */}
        <div className="tw-mb-10 tw-mt-2">
          <motion.h3
            className="tw-mb-4 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-widest tw-text-gray-400"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ ...SPRING_BOUNCE, delay: 0.1 }}
          >
            {t("leader_programme.cta.timeline_header")}
          </motion.h3>
          {/* Desktop: horizontal */}
          <div className="tw-hidden md:tw-flex md:tw-items-start md:tw-justify-center md:tw-gap-0">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.key}>
                <motion.div
                  className="tw-flex tw-w-40 tw-flex-col tw-items-center tw-text-center"
                  custom={i}
                  variants={timelineVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  <div className="tw-mb-2 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-600 tw-bg-gray-800 tw-text-xs tw-font-bold tw-text-white">
                    {i + 1}
                  </div>
                  {step.duck && (
                    <img
                      src={step.duck}
                      alt=""
                      className="tw-mb-1 tw-h-10 tw-w-10 tw-object-contain"
                    />
                  )}
                  <p className="tw-text-s tw-leading-snug tw-text-gray-300">
                    {getStepText(step)}
                  </p>
                </motion.div>
                {i < STEPS.length - 1 && (
                  <div className="tw-mt-4 tw-flex tw-w-8 tw-items-center tw-justify-center">
                    <div className="tw-h-px tw-w-full tw-bg-gray-600" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile: vertical */}
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-0 tw-pl-4 md:tw-hidden">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.key}>
                <motion.div
                  className="tw-flex tw-items-center tw-gap-3"
                  custom={i}
                  variants={timelineVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  <div className="tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-600 tw-bg-gray-800 tw-text-xs tw-font-bold tw-text-white">
                    {i + 1}
                  </div>
                  {step.duck && (
                    <img src={step.duck} alt="" className="tw-h-8 tw-w-8 tw-object-contain" />
                  )}
                  <p className="tw-text-left tw-text-sm tw-text-gray-300">
                    {getStepText(step)}
                  </p>
                </motion.div>
                {i < STEPS.length - 1 && (
                  <div className="tw-ml-[13px] tw-h-4 tw-w-px tw-bg-gray-600" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <motion.div
          className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 sm:tw-flex-row"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ ...SPRING_BOUNCE, delay: 0.4 }}
        >
          {/* Primary CTA */}
          <motion.button
            className="hover:tw-bg-primary-dark tw-w-full tw-rounded-full tw-bg-primary-main tw-px-8 tw-py-4 tw-text-base tw-font-bold tw-text-white tw-transition-all tw-duration-300 hover:tw-scale-[1.02] sm:tw-w-auto md:tw-text-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleApplyClick}
          >
            {t("leader_programme.cta.primary_button")}
          </motion.button>
        </motion.div>

        {/* Soft closing */}
        <motion.p
          className="tw-mt-10 tw-text-sm tw-italic tw-text-gray-500 md:tw-mt-12 md:tw-text-base"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ ...SPRING_BOUNCE, delay: 0.6 }}
        >
          {t("leader_programme.cta.closing")}
        </motion.p>
      </div>
    </section>
  );
};

export default CallToReflection;
